import React, { useEffect, useRef, useState } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';

/**
 * FifteenDaysChapter — High-Energy Intensity Countdown (250vh pinned)
 *
 * Scroll drives an intense compression sequence:
 * 0–70%:  Days count rapidly from 15 → 1. Video counter fills.
 *          Formulas scatter. Progress bar fills. Viewport densifies.
 * 70–75%: STOP. Everything clears instantly.
 * 75–85%: Dark screen. One word: "FAST-NUCES"
 * 85–100%: "GPA 3.89 / 4.00" — achievement lands.
 *
 * Interactive day inspector preserved below the pinned section.
 */
const FifteenDaysChapter = () => {
  const wrapperRef = useRef(null);
  const sceneRef = useRef(null);
  const annotationRef = useRef(null);
  const dayNumRef = useRef(null);
  const progressFillRef = useRef(null);
  const videoCountRef = useRef(null);
  const formulasRef = useRef(null);
  const intensityRef = useRef(null);
  const fastNameRef = useRef(null);
  const gpaRef = useRef(null);
  const [activeDay, setActiveDay] = useState(15);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let ctx;
    const setup = () => {
      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top top',
            end: '+=180%',
            pin: sceneRef.current,
            scrub: 0.8,
            anticipatePin: 1,
            onUpdate: (self) => {
              // Drive day number from scroll progress
              const prog = self.progress;
              if (prog < 0.65 && dayNumRef.current) {
                const day = Math.max(1, Math.round(15 - prog / 0.65 * 14));
                dayNumRef.current.textContent = `DAY ${String(day).padStart(2, '0')}`;
              }
              // Drive video count
              if (prog < 0.65 && videoCountRef.current) {
                const vids = Math.round(prog / 0.65 * 150);
                videoCountRef.current.textContent = vids;
              }
              // Drive progress bar
              if (progressFillRef.current) {
                const pct = Math.min(100, Math.round(prog / 0.65 * 100));
                progressFillRef.current.style.width = `${pct}%`;
              }
            }
          }
        });

        // 0–10%: annotation + scene appears
        tl.fromTo(annotationRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.3 },
          0
        )
        .fromTo(intensityRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.5 },
          0.1
        )

        // 10–65%: formulas scatter around
        .fromTo(formulasRef.current.children,
          { opacity: 0, scale: 0.5, x: (i) => (Math.random() - 0.5) * 80, y: (i) => (Math.random() - 0.5) * 60 },
          {
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0,
            stagger: { amount: 1.2, from: 'random' },
            ease: 'power2.out',
            duration: 0.3
          },
          0.2
        )

        // 65–70%: STOP — everything clears
        .to([intensityRef.current, formulasRef.current, annotationRef.current], {
          opacity: 0,
          scale: 0.95,
          duration: 0.15,
          ease: 'power3.in'
        }, 2.0)

        // 70–85%: FAST name reveals
        .fromTo(fastNameRef.current,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, ease: 'power4.out', duration: 0.4 },
          2.2
        )

        // 85–95%: Shift FAST name up & bring GPA in cleanly below it
        .to(fastNameRef.current, {
          y: '-12vh',
          scale: 0.95,
          opacity: 0.95,
          ease: 'power2.inOut',
          duration: 0.5
        }, 2.7)
        .fromTo(gpaRef.current,
          { opacity: 0, y: '5vh' },
          { opacity: 1, y: '2vh', ease: 'power3.out', duration: 0.5 },
          2.8
        )

        // 95–100%: Fade out FAST name & GPA as user scrolls down into Chapter 7
        .to([fastNameRef.current, gpaRef.current], {
          opacity: 0,
          y: '-10vh',
          ease: 'power2.in',
          duration: 0.6
        }, 3.5);
      });
    };

    ScrollTrigger.refresh();
    setup();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  const getDayDetails = (day) => {
    if (day === 1) {
      return { title: 'ENTRY TEST DAY', desc: 'All 15 days of prep put to the test.', videos: 150, hours: 18 };
    }
    const videos = Math.min(150, Math.round((16 - day) * 10.5));
    return {
      title: `INTENSIVE STUDY BLOCK`,
      desc: `Watching 11+ Hashim Zia videos daily. Taking screenshots instead of normal notes.`,
      videos,
      hours: Math.min(18, 12 + Math.floor((16 - day) * 0.4))
    };
  };

  const details = getDayDetails(activeDay);

  const formulas = [
    'f(x) = x² + 2x + 1', 'PV = nRT', 'E = mc²', 'F = ma',
    'sin²θ + cos²θ = 1', 'a² + b² = c²', 'v = u + at',
    'P = IV', 'ΔS ≥ 0', 'λ = h/mv'
  ];

  return (
    <div className="fifteen-chapter-outer">
      {/* Pinned intensity scene */}
      <div ref={wrapperRef} className="fifteen-scroll-wrapper">
        <div ref={sceneRef} className="fifteen-scene">
          <div className="fifteen-bg" aria-hidden="true" />

          <div ref={annotationRef} className="fifteen-annotation font-mono">
            CH. 06 &nbsp;// 15 DAYS
          </div>

          {/* Intensity block */}
          <div ref={intensityRef} className="fifteen-intensity">
            <div ref={dayNumRef} className="fifteen-day-num font-display">DAY 15</div>

            <div className="fifteen-progress-container">
              <div className="fifteen-progress-label font-mono">
                <span>VIDEOS WATCHED</span>
                <span>
                  <span ref={videoCountRef} className="fifteen-video-count">0</span>
                  &nbsp;/ 150
                </span>
              </div>
              <div className="fifteen-progress-track">
                <div ref={progressFillRef} className="fifteen-progress-fill" />
              </div>
            </div>

            <div className="fifteen-hours-label font-mono">
              11+ HOURS OF VIDEO PER DAY &nbsp;·&nbsp; SCREENSHOT NOTES
            </div>
          </div>

          {/* Floating formulas */}
          <div ref={formulasRef} className="fifteen-formulas font-mono" aria-hidden="true">
            {formulas.map((f, i) => (
              <div
                key={i}
                className="fifteen-formula"
                style={{
                  top: `${10 + (i * 8) % 75}%`,
                  left: `${5 + (i * 13) % 85}%`,
                  opacity: 0.6 + (i % 3) * 0.1,
                  fontSize: `${0.65 + (i % 4) * 0.08}rem`
                }}
              >
                {f}
              </div>
            ))}
          </div>

          {/* FAST reveal */}
          <div ref={fastNameRef} className="fifteen-fast-reveal">
            <div className="fifteen-fast-text font-display">FAST-NUCES</div>
          </div>

          {/* GPA */}
          <div ref={gpaRef} className="fifteen-gpa">
            <div className="fifteen-gpa-value font-display">3.89<span className="fifteen-gpa-denom"> / 4.00</span></div>
            <div className="fifteen-gpa-label font-mono">CUMULATIVE GPA</div>
            <div className="fifteen-gpa-sub font-mono">
              People see the number.<br />
              They don't see the 18-hour study blocks behind it.
            </div>
          </div>
        </div>
      </div>

      {/* Interactive day inspector — clean block below pinned scene */}
      <div className="fifteen-inspector">
        <div className="fifteen-insp-container">
          <div className="fifteen-insp-header font-mono">
            <span>INTENSIVE PREPARATION TIMELINE</span>
            <span className="fifteen-insp-active">DAY {String(activeDay).padStart(2, '0')}</span>
          </div>

          <div className="fifteen-days-track">
            {Array.from({ length: 15 }, (_, i) => 15 - i).map((d) => (
              <button
                key={d}
                className={`fifteen-day-chip font-mono${d === activeDay ? ' active' : ''}`}
                onClick={() => setActiveDay(d)}
              >
                {d === 1 ? 'TEST' : `D${d}`}
              </button>
            ))}
          </div>

          <div className="fifteen-insp-body">
            <div className="fifteen-insp-title font-mono">{details.title}</div>
            <p className="fifteen-insp-desc">{details.desc}</p>
            <div className="fifteen-insp-metrics">
              <div className="fifteen-metric">
                <span className="fifteen-metric-val font-mono">{details.videos}</span>
                <span className="fifteen-metric-label">Videos Watched</span>
              </div>
              <div className="fifteen-metric">
                <span className="fifteen-metric-val font-mono">{details.hours}h</span>
                <span className="fifteen-metric-label">Daily Session</span>
              </div>
              <div className="fifteen-metric">
                <span className="fifteen-metric-val font-mono">100%</span>
                <span className="fifteen-metric-label">Screenshot Notes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .fifteen-chapter-outer {
          position: relative;
          background: #080a0f;
        }

        .fifteen-scroll-wrapper {
          position: relative;
          height: 180vh;
          background: #080a0f;
        }

        .fifteen-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform;
        }

        .fifteen-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 50%, rgba(212,163,89,0.03) 0%, transparent 70%);
          pointer-events: none;
        }

        .fifteen-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.5);
          will-change: opacity;
        }

        .fifteen-intensity {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          z-index: 5;
          will-change: opacity, transform;
        }

        .fifteen-day-num {
          font-size: clamp(4rem, 12vw, 10rem);
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #f3f4f6;
          line-height: 1;
          transition: color 0.1s;
        }

        .fifteen-progress-container {
          width: min(400px, 80vw);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .fifteen-progress-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.65rem;
          letter-spacing: 0.14em;
          color: rgba(107, 114, 128, 0.8);
        }

        .fifteen-video-count {
          color: var(--accent-gold);
          font-weight: 700;
        }

        .fifteen-progress-track {
          width: 100%;
          height: 3px;
          background: rgba(255,255,255,0.07);
          border-radius: 3px;
          overflow: hidden;
        }

        .fifteen-progress-fill {
          height: 100%;
          width: 0%;
          background: linear-gradient(to right, rgba(212,163,89,0.6), rgba(212,163,89,1));
          border-radius: 3px;
          transition: width 0.05s;
        }

        .fifteen-hours-label {
          font-size: 0.62rem;
          letter-spacing: 0.16em;
          color: rgba(107, 114, 128, 0.5);
        }

        /* Formulas */
        .fifteen-formulas {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
        }

        .fifteen-formula {
          position: absolute;
          color: rgba(212, 163, 89, 0.18);
          letter-spacing: 0.05em;
          white-space: nowrap;
          will-change: opacity, transform;
        }

        /* FAST reveal */
        .fifteen-fast-reveal {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: opacity, transform;
          z-index: 10;
        }

        .fifteen-fast-text {
          font-size: clamp(2.8rem, 6.8vw, 5.8rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #f3f4f6;
          white-space: nowrap;
        }

        /* GPA */
        .fifteen-gpa {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          will-change: opacity, transform;
          z-index: 10;
        }

        .fifteen-gpa-value {
          font-size: clamp(2.5rem, 6vw, 5rem);
          font-weight: 800;
          color: var(--accent-gold);
          letter-spacing: -0.02em;
          line-height: 1;
        }

        .fifteen-gpa-denom {
          font-size: 0.5em;
          color: rgba(212,163,89,0.5);
        }

        .fifteen-gpa-label {
          font-size: 0.65rem;
          letter-spacing: 0.2em;
          color: rgba(107,114,128,0.7);
          margin-top: 0.5rem;
        }

        .fifteen-gpa-sub {
          font-size: 0.75rem;
          letter-spacing: 0.06em;
          color: rgba(156,163,175,0.6);
          margin-top: 1rem;
          line-height: 1.6;
        }

        /* Inspector below */
        .fifteen-inspector {
          padding: clamp(3rem, 8vw, 6rem) clamp(1.25rem, 5vw, 4rem);
          background: #0e121b;
        }

        .fifteen-insp-container {
          max-width: 800px;
          margin: 0 auto;
          background: rgba(16, 21, 31, 0.75);
          border: 1px solid rgba(212,163,89,0.15);
          border-radius: 12px;
          padding: 2rem;
        }

        .fifteen-insp-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.25rem;
          font-size: 0.65rem;
          letter-spacing: 0.14em;
          color: rgba(107,114,128,0.7);
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .fifteen-insp-active {
          color: var(--accent-gold);
          font-weight: 700;
        }

        .fifteen-days-track {
          display: flex;
          gap: 0.35rem;
          flex-wrap: wrap;
          margin-bottom: 1.25rem;
        }

        .fifteen-day-chip {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: rgba(156,163,175,0.7);
          padding: 0.3rem 0.5rem;
          border-radius: 5px;
          cursor: pointer;
          font-size: 0.62rem;
          letter-spacing: 0.06em;
          white-space: nowrap;
          transition: all 0.2s ease;
        }

        .fifteen-day-chip.active {
          background: var(--accent-gold);
          color: #080a0f;
          border-color: var(--accent-gold);
          font-weight: 700;
        }

        .fifteen-insp-body {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding-top: 1.25rem;
        }

        .fifteen-insp-title {
          font-size: 0.72rem;
          letter-spacing: 0.14em;
          color: var(--accent-gold);
          margin-bottom: 0.5rem;
        }

        .fifteen-insp-desc {
          font-size: 0.875rem;
          color: rgba(156,163,175,0.8);
          line-height: 1.6;
          margin-bottom: 1.25rem;
        }

        .fifteen-insp-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .fifteen-metric {
          background: rgba(0,0,0,0.25);
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.05);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.3rem;
        }

        .fifteen-metric-val {
          font-size: 1.5rem;
          color: var(--accent-gold);
          font-weight: 700;
        }

        .fifteen-metric-label {
          font-size: 0.7rem;
          color: rgba(107,114,128,0.7);
          text-align: center;
        }

        @media (max-width: 768px) {
          .fifteen-scroll-wrapper {
            height: 300vh;
          }

          /* Annotation text safely below mobile topbar */
          .fifteen-annotation {
            top: calc(56px + 12px);
            left: 5vw;
            font-size: 0.62rem;
            letter-spacing: 0.16em;
          }

          /* Day counter scale on mobile */
          .fifteen-day-num {
            font-size: clamp(3.2rem, 16vw, 6rem);
          }

          /* FAST text scales cleanly to fit mobile screen */
          .fifteen-fast-text {
            font-size: clamp(1.6rem, 8vw, 2.4rem);
            letter-spacing: -0.02em;
            text-align: center;
            max-width: 90vw;
          }

          /* GPA text on mobile */
          .fifteen-gpa-value {
            font-size: clamp(2.2rem, 9vw, 3.2rem);
          }

          .fifteen-gpa-label {
            font-size: 0.58rem;
            letter-spacing: 0.16em;
          }

          .fifteen-gpa-sub {
            font-size: 0.7rem;
            max-width: 88vw;
            line-height: 1.5;
          }

          .fifteen-inspector {
            padding: 2.5rem 1rem 4rem 1rem;
            margin-bottom: 3rem;
          }

          .fifteen-insp-container {
            padding: 1.25rem;
          }

          /* Metrics — single column on mobile screens */
          .fifteen-insp-metrics {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          /* Text wraps cleanly */
          .fifteen-text,
          .fifteen-headline {
            white-space: normal;
            text-align: center;
            padding: 0 1.25rem;
          }

          .fifteen-headline {
            font-size: clamp(1.5rem, 7vw, 2.2rem);
          }
        }
      `}</style>
    </div>
  );
};

export default FifteenDaysChapter;
